import { Component, inject, ElementRef, Renderer2, viewChild, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, ViewportScroller } from '@angular/common';
import { AppLanguage, IRolesFilter, NavItem, UserRole } from '../models';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { GeneralState, D4CActions, Selectors } from '../store';
import { SetLanguage, LanguagePrefixPipe } from '@ca/ca-ng-core';
import { selectCurrentRoute, selectHomeName, selectLanguage, selectUserRole } from '../store/selectors';
import { CaSubscriber } from '@ca/ca-utils';
import { IsStringValue } from '@ca/core';
import { RouterReducerState } from '@ngrx/router-store';
import { SetRole } from '../store/actions';

@Component({
  selector: 'ca-header',
  imports: [CommonModule, RouterModule, LanguagePrefixPipe],
  template: `
    @if (data$ | async; as data) {
      <nav class="nav header-navigation">
        <a id="design4circularity" [routerLink]="[] | languagePrefix | async" (click)="resetRole()" (mouseenter)="active = null">
          <img src="header-logo.svg" alt="Valumat & Wood - combined logo" />
        </a>

        <div id="head-nav" class="nav-list roles-dropdowns">
          @for (filter of data.menuTriggers; track filter.filter) {
            <a
              (mouseenter)="active = filter.filter"
              [class.active]="active == filter.filter"
              [class.active-role]="data.role == filter.filter"
              [routerLink]="[] | languagePrefix | async"
              (click)="menuClick()">
              <span class="material-icons material-symbols-outlined">keyboard_arrow_down</span>
              {{ filter.name }}
            </a>
          }
          <div class="selector">
            <select name="language-selector" id="language-selector" (mouseenter)="active = null" (change)="onLanguageChange($event)" [value]="language">
              <option value="en" >English</option>
              <option value="nl">Nederlands</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>

        <div id="right-nav">
          <a class="search-btn" [routerLink]="['search'] | languagePrefix | async" (click)="active = null" [class.active]="isActiveRoute(['search'])">{{
            searchLabel$ | async
          }}</a>

          <a [routerLink]="['contact'] | languagePrefix | async" (click)="active = null" [class.active]="isActiveRoute(['contact'])">{{
            contactLabel$ | async
          }}</a>
        </div>

        <!-- MOBILE NAV -->
        <div #mobileMenuContent [class.closed]="!isMobileMenuOpen" [class.active]="isMobileMenuOpen" id="mobileMenuContent">
          <ul class="roles-dropdowns">
            @for (filter of data.menuTriggers; track filter.filter) {
              <li (click)="toggleFilter(filter.filter)">
                <h3>{{ filter.name }}</h3>
                @if (expandedFilter === filter.filter || (role$|async) == filter.filter) {
                  <div class="extra-nav">
                    <a (click)="setRoleAndCloseMenu(filter.name)" class="role-home-link" [routerLink]="[] | languagePrefix | async">{{homeLabel$|async}}</a>

                    @for (group of data.mainMenuGroups; track group.uuid) {
                      <span>{{group.title}}</span>
                      <ng-container *ngTemplateOutlet="renderGroup; context: {group: group, role: filter.filter}"></ng-container>
                    }
                  </div>
                }
              </li>
            }

            <li>
              <div class="extra-nav subnavi">
                <a class="search-btn" (click)="closeMobileMenuWithAction()" [routerLink]="['search'] | languagePrefix | async" [class.active]="isActiveRoute(['search'])">{{searchLabel$ | async}}</a>
                <a (click)="closeMobileMenuWithAction()" [routerLink]="['contact'] | languagePrefix | async" [class.active]="isActiveRoute(['contact'])">{{ contactLabel$ | async }}</a>
                <div class="language-selector">
                  <a (click)="setLanguage('en')" [class.active]="language == 'en'">EN</a>
                  <a (click)="setLanguage('nl')" [class.active]="language == 'nl'">NL</a>
                  <a (click)="setLanguage('fr')" [class.active]="language == 'fr'">FR</a>
                </div>
              </div>
            </li>

          </ul>
        </div>

        <div class="mobile-menu" (click)="toggleMobileMenu()" [style.position]="isMobileMenuOpen ? 'fixed' : 'unset'">
          <span class="material-icons material-symbols-outlined">menu</span>
        </div>
          <!-- END MOBILE NAV -->
      </nav>

      @if (active) {
        <menu role="menu" class="dropdown-menu" (mouseleave)="active = null">
          <div class="left">
            @for (group of data.mainMenuGroups; track $index) {
              @if (group[active]) {
                <span>{{ group.title }}</span>
                <ul class="nav-items">
                  @for (item of group.items; track $index) {
                    @if (item[active]) {
                      <li>
                        <a
                          (mouseenter)="openSubMenu = { item: item, i: $index }"
                          (click)="menuClick()"
                          [class.active]="isActiveRoute(item.routerLink)"
                          [routerLink]="item.routerLink | languagePrefix | async"
                          >{{ item.title }}</a
                        >
                      </li>
                    }
                  }
                </ul>
              }
            }
          </div>

          <div class="right">
            @if (openSubMenu?.item?.groups) {
              @for (subItem of openSubMenu?.item?.groups; track subItem.uuid) {
                <ul class="sub-nav nav-items">
                  @if (subItem[active]) {
                    <li class="sub-nav-item">{{ subItem.title }}</li>
                    @for (childItem of subItem.items; track childItem.metaUrl) {
                      @if (childItem[active]) {
                        <li class="child-nav-item">
                          <a
                            (click)="menuClick()"
                            [class.active]="isActiveRoute(childItem.routerLink)"
                            [routerLink]="childItem.routerLink | languagePrefix | async"
                            >{{ childItem.title }}</a
                          >
                        </li>
                      }
                    }
                  }
                </ul>
              }
            }
          </div>
        </menu>
      }
    }

    <ng-template #renderGroup let-group="group" let-role="role" >
      <ul class="nav-items">
        @for (item of group.items; track item.uuid) {
            <li>
              <a (click)="setRoleAndCloseMenu(role)" [routerLink]="item.routerLink | languagePrefix | async" [class.active]="isActiveRoute(item.routerLink)">{{ item.title }}</a>
              @if (item.groups) {
                @for (subGroup of item.groups; track subGroup.uuid) {
                  <ng-container *ngTemplateOutlet="renderGroup; context:  {group: subGroup, role: role }"></ng-container>
                }
              }
            </li>
          }
      </ul>
    </ng-template>
  `,
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject(Store<{ general: GeneralState; router: RouterReducerState; }>);
  private scroller = inject(ViewportScroller);
  active: keyof IRolesFilter | null = null;
  role$ = this.store.select(selectUserRole);
  openSubMenu: { item: NavItem; i: number } | null = null;
  expandedSubItems: Record<string, boolean> = {};
  contactLabel$ = this.store.select((s) => s.general.contactLabel);
  searchLabel$ = this.store.select((s) => s.general.searchLabel);
  homeLabel$ = this.store.select(selectHomeName);
  data$ = this.store.select(Selectors.selectHeaderData);
  menuContent = viewChild<ElementRef<HTMLElement>>('mobileMenuContent');
  language$ = this.store.select(selectLanguage);
  language = 'nl';
  currentRoute: string = '';

  expandedFilter: string | null = null;
  isMobileMenuOpen = false;
  private sub = new CaSubscriber();

  private currentRouteObserver = (currentRoute: string[]) => {
    this.currentRoute = '/' + currentRoute.slice(1).join('/');
    this.active = null;
  };

  ngOnInit(): void {
    this.sub.subscribe(this.language$, (lang) => { this.language = lang; });
    this.sub.subscribe(this.store.select(selectCurrentRoute), this.currentRouteObserver);
  }

  ngAfterViewInit() {
    if (!this.menuContent) {
      console.error('Error: menuContent is not available');
    }
  }

  ngOnDestroy(): void {
    this.sub.closeSubscriptions();
  }

  resetRole() {
    this.store.dispatch(D4CActions.SetRole({ role: null }));
    this.active = null;
    if (this.currentRoute.split('/').filter(IsStringValue).length === 0) {
      this.scroller.scrollToPosition([0, 0]);
    }
  }

  menuClick() {
    this.store.dispatch(D4CActions.SetRole({ role: this.active }));
    this.active = null;
  }

  onLanguageChange(e: Event) {
    const selectElement = e.target as HTMLSelectElement;
    const language = selectElement.value as AppLanguage;
    this.setLanguage(language)
  }

  setLanguage(language: string) {
    this.store.dispatch(SetLanguage({ language }));
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenuWithAction() {
    this.menuClick();
    this.isMobileMenuOpen = false;
  }

  toggleSubMenuItem(subItem: string, event: Event) {
    event.stopPropagation();
    this.expandedSubItems[subItem] = !this.expandedSubItems[subItem];
  }

  toggleFilter(filter: string) {
    this.expandedFilter = this.expandedFilter === filter ? null : filter;
  }

  isActiveRoute(route: string | string[]): boolean {
    const target = Array.isArray(route) ? '/' + route.join('/') : route;
    return this.currentRoute === target || this.currentRoute.startsWith(target + '/');
  }

  setRoleAndCloseMenu(role: string) {
    this.closeMobileMenuWithAction()
    if (role) this.store.dispatch(SetRole({ role: role as UserRole }));
  }
}
